// viewerAkMode — mały admin (viewer-admin) w trybie AK (MONIT 2026-10-13_1130, etap E7a; pkt 2.C-D).
//
// Mały admin dostaje przełącznik MO / AK jak pełny Admin (ten sam klucz `fiberops_admin_mode`, telefon pamięta tryb).
// W trybie AK KAŻDE żądanie do API niesie nagłówek `X-FiberOps-Mode: ak` (useApi + kolejka zdjęć: wpis zapamiętuje
// tryb z chwili zrobienia zdjęcia). Serwer (services/viewerAk.js) na trasach adresu traktuje go wtedy jak technika
// powiązanego teamu AK, na trasach listy zostawia małym adminem (lista włączonych teamów).
// Kontekst (powiązany team, okno AK, teamy) z GET /api/ak/context leży w localStorage `fiberops_viewer_ak_ctx`
// (offline: telefon wie, czy działa jako team AK, zanim API odpowie). Kontekst innego konta = ignorowany.
// „Działa jako team AK” (acting) = tryb AK + powiązany team aktywny: wtedy useAuth podaje ekranom adresu rolę technika
// AK (isAk = true, isAdmin = false), więc kreator, zdjęcia i status wyglądają jak u technika AK.
// Czyste funkcje + mała szyna zdarzeń (node --test).

import { readAdminMode } from './adminMode.js';

export const VIEWER_AK_HEADER = 'X-FiberOps-Mode';
export const VIEWER_AK_CTX_KEY = 'fiberops_viewer_ak_ctx';

/** Czy sesja to konto małego admina (rola z logowania, niezależnie od trybu). */
export function isViewerAccount(team) {
  return team?.role === 'viewer-admin';
}

/** Tryb AK małego admina (z pamięci telefonu). */
export function viewerAkModeOn(team, storage) {
  return isViewerAccount(team) && readAdminMode(storage) === 'ak';
}

/** Kontekst z pamięci; null, gdy brak, śmieci albo zapisany dla innego konta (inna nazwa w sesji). */
export function readViewerAkContext(storage, team) {
  try {
    const raw = storage?.getItem?.(VIEWER_AK_CTX_KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw);
    if (!ctx || typeof ctx !== 'object') return null;
    if (team && ctx.owner !== (team.name || null)) return null;
    return ctx;
  } catch {
    return null;
  }
}

export function writeViewerAkContext(storage, team, ctx) {
  try {
    if (!ctx) storage?.removeItem?.(VIEWER_AK_CTX_KEY);
    else storage?.setItem?.(VIEWER_AK_CTX_KEY, JSON.stringify({ ...ctx, owner: team?.name || null, savedAt: Date.now() }));
  } catch { /* bez pamięci: kontekst do przeładowania */ }
}

/** Powiązany team z kontekstu ({ id, label, active }) albo null. */
export function linkedTeamOf(ctx) {
  const l = ctx?.viewer?.linked;
  return l && typeof l.id === 'string' ? l : null;
}

/** Czy mały admin działa teraz jako team AK (tryb AK + aktywne powiązanie). */
export function viewerAkActing(team, storage, ctx = readViewerAkContext(storage, team)) {
  if (!viewerAkModeOn(team, storage)) return false;
  const l = linkedTeamOf(ctx);
  return !!(l && l.active);
}

/**
 * Sesja widziana przez ekrany (useAuth): mały admin działający jako team AK = technik AK (rola, rodzaj, nazwa teamu);
 * każdy inny przypadek = sesja bez zmian. `account` = prawdziwa rola z logowania.
 */
export function effectiveTeam(team, storage, ctx = readViewerAkContext(storage, team)) {
  if (!viewerAkActing(team, storage, ctx)) return team;
  const l = linkedTeamOf(ctx);
  return {
    ...team,
    isAdmin: false,
    role: 'technik',
    kind: 'ak',
    name: l.label,
    airtableRecordId: l.id,
    viaViewer: team.name || null,
  };
}

/** Nagłówki trybu do żądania (pusty obiekt poza trybem AK małego admina). */
export function viewerAkHeaders(team, storage) {
  return viewerAkModeOn(team, storage) ? { [VIEWER_AK_HEADER]: 'ak' } : {};
}

/** Segment klucza cache GET: tryb AK małego admina ma osobny (inne prawa na trasach adresu). */
export function cacheScopeFor(team, storage) {
  if (isViewerAccount(team)) return viewerAkModeOn(team, storage) ? 'viewer-ak' : 'viewer';
  return team?.isAdmin ? 'admin' : 'user';
}

/**
 * Stan karty AK u małego admina (`viewerAk` z serwera: { own, readOnly }):
 *   own + !readOnly = pełne prawa technika AK (status, zdjęcia, konfiguracja, „Wyślij teraz”, „Przekaż termin”),
 *   inaczej = tylko odczyt (plakietka teamu, 🔒, AK Info).
 * Pełny Admin (brak `viewerAk`) = pełne prawa jak dotąd.
 */
export function akCardAccess(task) {
  const v = task?.viewerAk;
  if (!v) return { readOnly: false, own: false, viewer: false };
  return { readOnly: !!v.readOnly, own: !!v.own, viewer: true };
}

// ── Szyna zdarzeń: zmiana trybu / kontekstu (useAuth przerysowuje ekrany) ──────────────────────────────────
const listeners = new Set();
export function subscribeViewerAk(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function notifyViewerAk() {
  for (const fn of listeners) {
    try { fn(); } catch { /* słuchacz nie psuje innych */ }
  }
}
